import React, { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { costApi, vehicleApi } from '../../api/portal';
import usePortalQuery, { invalidatePortal } from '../../hooks/usePortalQuery';
import { QueryStatus } from '../../components/portal/PortalUI';

const money = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
const validDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};
const emptyDraft = () => ({ date: '', amount: '', title: '', description: '' });
const draftFrom = (cost) => ({ date: cost.date, amount: String(cost.amount), title: cost.title, description: cost.description });
const normalizedAmount = (value) => {
  const numeric = Number(value);
  return Math.round((numeric + Number.EPSILON) * 100) / 100;
};

export default function VehicleCosts() {
  const { profileId } = useParams();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const list = usePortalQuery(['worker-vehicle-list', q, offset], (signal) => vehicleApi.list({ q, offset, limit: 50 }, signal), !profileId);
  const selectedQuery = usePortalQuery(['worker-vehicle', profileId], (signal) => vehicleApi.get(profileId, signal), !!profileId);
  const selected = selectedQuery.data?.profile;

  if (!profileId) return <main className="portal"><header className="portal-header">
    <h1>Additional costs</h1><p>Select a car to record expenses not included in an uploaded report.</p>
  </header><QueryStatus query={list} />
    <form className="portal-controls" onSubmit={(event) => { event.preventDefault(); setOffset(0); setQ(search.trim()); }}>
      <label>Search cars<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Plate, make, or model" /></label>
      <button>Search</button>
    </form>
    {list.data && (list.data.vehicle_profiles.length ? <section className="portal-card"><div className="portal-table-scroll"><table>
      <caption>Select a car ({list.data.count} matches)</caption><thead><tr><th>Plate</th><th>Make / model</th><th>Year</th><th>Additional costs</th></tr></thead>
      <tbody>{list.data.vehicle_profiles.map(({ profile }) => <tr key={profile.id}><td>{profile.plate_number || profile.id}</td>
        <td>{[profile.Make || profile.make, profile.Model || profile.model].filter(Boolean).join(' ') || '-'}</td><td>{profile.year || '-'}</td>
        <td><Link to={`/worker/vehicles/${encodeURIComponent(profile.id)}/costs`}>Manage costs</Link></td></tr>)}</tbody>
    </table></div>
      <div className="portal-actions"><button type="button" disabled={offset <= 0} onClick={() => setOffset(Math.max(0, offset - 50))}>Previous</button>
        <span>Showing {offset + 1}–{Math.min(offset + list.data.vehicle_profiles.length, list.data.count)} of {list.data.count}</span>
        <button type="button" disabled={offset + list.data.vehicle_profiles.length >= list.data.count} onClick={() => setOffset(offset + 50)}>Next</button></div>
    </section> : <p>{q ? 'No cars match your search.' : 'No cars available.'}</p>)}
  </main>;

  return <main className="portal"><header className="portal-header">
    <p><Link to="/worker/vehicles">All cars</Link></p><h1>Additional costs</h1>
    <p>{selected?.plate_number || profileId}{selected && ` | ${[selected.Make, selected.Model].filter(Boolean).join(' ')}`}</p>
  </header>
    <QueryStatus query={selectedQuery} />
    {selected && <CostManager key={profileId} profileId={profileId} />}
  </main>;
}

export function CostManager({ profileId }) {
  const query = usePortalQuery(['vehicle-costs', profileId], (signal) => costApi.list(profileId, signal));
  const [draft, setDraft] = useState(emptyDraft());
  const [editingId, setEditingId] = useState('');
  const [editingEtag, setEditingEtag] = useState('');
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [latest, setLatest] = useState(null);

  const edit = (cost) => {
    setEditingId(cost.id); setEditingEtag(cost.etag); setDraft(draftFrom(cost)); setError(''); setMessage(''); setLatest(null);
  };
  const cancel = () => {
    setEditingId(''); setEditingEtag(''); setDraft(emptyDraft()); setError(''); setLatest(null);
  };
  const reloadLatest = async (costId) => {
    query.refresh();
    if (costId) {
      try {
        const records = await costApi.list(profileId);
        setLatest(records.find((item) => item.id === costId) || null);
      } catch (loadError) {
        setError(`The latest cost could not be loaded: ${loadError.message}`);
      }
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (busyRef.current) return;
    const amount = normalizedAmount(draft.amount);
    if (!validDate(draft.date)) { setError('Enter a valid calendar date.'); return; }
    if (!Number.isFinite(amount) || amount <= 0) { setError('Amount must be positive after rounding to cents.'); return; }
    if (!draft.title.trim() || !draft.description.trim()) { setError('Title and description are required.'); return; }
    const payload = { date: draft.date, amount, title: draft.title.trim(), description: draft.description.trim() };
    busyRef.current = true; setBusy(true); setError(''); setMessage('');
    try {
      if (editingId) {
        if (!editingEtag) {
          await reloadLatest(editingId);
          throw new Error('This cost has no current ETag. It was not changed; reload and review it before retrying.');
        }
        await costApi.update(profileId, editingId, payload, editingEtag);
        setMessage('Additional cost updated.');
      } else {
        await costApi.create(profileId, payload);
        setMessage('Additional cost added.');
      }
      cancel(); invalidatePortal();
    } catch (requestError) {
      if ([404, 409, 428].includes(requestError.status)) {
        await reloadLatest(editingId);
        const detail = requestError.status === 409 ? 'The cost changed elsewhere. Review the latest saved version before editing again.' :
          requestError.status === 428 ? 'The server requires a fresh ETag. Review the latest saved version before editing again.' :
            'This cost is no longer available. The list has been refreshed.';
        setError(`${detail} ${requestError.message}`);
      } else setError(requestError.message || 'Unable to save this cost.');
    } finally { busyRef.current = false; setBusy(false); }
  };
  const remove = async () => {
    if (!deleting || busyRef.current) return;
    if (!deleting.etag) {
      await reloadLatest(deleting.id);
      setError('This cost has no current ETag. It was not removed; reload and review it before retrying.');
      setDeleting(null); return;
    }
    busyRef.current = true; setBusy(true); setError(''); setMessage('');
    try {
      await costApi.remove(profileId, deleting.id, deleting.etag);
      setMessage('Additional cost removed.'); setDeleting(null); invalidatePortal();
    } catch (requestError) {
      if ([404, 409, 428].includes(requestError.status)) {
        await reloadLatest(deleting.id);
        setError(`${requestError.status === 409 ? 'The cost changed elsewhere. Review the latest saved version before taking action.' :
          requestError.status === 428 ? 'The server requires a fresh ETag. Review the latest saved version before taking action.' :
            'This cost is no longer available. The list has been refreshed.'} ${requestError.message}`);
        setDeleting(null);
      } else setError(requestError.message || 'Unable to remove this cost.');
    } finally { busyRef.current = false; setBusy(false); }
  };
  return <section className="portal-card">
    <h2>Additional costs</h2>
    <p className="portal-warning">Only enter expenses not already included in the uploaded report. These costs are additional deductions.</p>
    {error && <p className="portal-error" role="alert">{error}</p>}
    {message && <p className="portal-success" role="status">{message}</p>}
    <QueryStatus query={query} />
    {latest && <section className="portal-warning" aria-label="Latest saved cost"><h3>Latest saved version — review before retrying</h3>
      <p>{latest.date} | {latest.title} | {money(latest.amount)} — {latest.description}</p>
      {editingId === latest.id ? <button type="button" onClick={() => { setEditingEtag(latest.etag); setLatest(null); setError(''); }}>
        I reviewed this version — continue editing my draft
      </button> : <button type="button" onClick={() => edit(latest)}>Edit latest version</button>}
    </section>}
    <form className="portal-form" onSubmit={submit}>
      <label>Date<input type="date" required value={draft.date} onChange={(event) => setDraft({ ...draft, date: event.target.value })} /></label>
      <label>Amount (USD)<input inputMode="decimal" type="text" required value={draft.amount} onChange={(event) => setDraft({ ...draft, amount: event.target.value })} /></label>
      <label>Title<input required value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} /></label>
      <label>Description<textarea required value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
      <div className="portal-actions"><button disabled={busy || query.loading}>{busy ? 'Saving...' : editingId ? 'Save changes' : 'Add cost'}</button>
        {editingId && <button type="button" disabled={busy} onClick={cancel}>Cancel edit</button>}</div>
    </form>
    {query.data && (query.data.length ? <div className="portal-table-scroll"><table><caption>Manual additional cost lines (USD)</caption>
      <thead><tr><th>Date</th><th>Title</th><th>Description</th><th>Amount</th><th>Actions</th></tr></thead>
      <tbody>{query.data.map((cost) => <tr key={cost.id}><td>{cost.date}</td><td>{cost.title}</td><td>{cost.description}</td><td>{money(cost.amount)}</td>
        <td><div className="portal-actions"><button type="button" disabled={busy} onClick={() => edit(cost)}>Edit</button>
          <button type="button" disabled={busy} onClick={() => { setDeleting(cost); setError(''); }}>Remove</button></div></td></tr>)}</tbody>
    </table></div> : <p>No additional costs recorded for this car.</p>)}
    {deleting && <section className="portal-warning" role="alertdialog" aria-label="Confirm cost removal">
      <p>Remove {deleting.date} — {deleting.title} ({money(deleting.amount)})? This cannot be undone.</p>
      <div className="portal-actions"><button type="button" disabled={busy} onClick={remove}>{busy ? 'Removing...' : 'Confirm remove'}</button>
        <button type="button" disabled={busy} onClick={() => setDeleting(null)}>Cancel</button></div>
    </section>}
  </section>;
}
